import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import { useNavigate } from "react-router";
import { useState } from "react";
import Pagination from "../ui/pagination/Pagination";

interface AbandonedCart {
    id: string;
    cartId: string;
    companyId: string;
    sessionId: string;
    email: string;
    phone: string;
    name: string | null;
    items: {
        id: string;
        price: string;
        title: string;
        quantity: number;
        productId: string;
    }[];
    status: string;
    createdAt: string;
    updatedAt: string;
    checkoutUrl?: string | null;
}

// Formatar valor monetário
const formatCurrency = (value: number | string) => {
    if (!value) return "";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
    }).format(num);
};

const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

interface AbandonedTableProps {
    carts?: AbandonedCart[];
    isLoading?: boolean;
    searchTerm?: string;
}

export default function SalesAbandonedTable({ searchTerm = "", carts = [], isLoading = false }: AbandonedTableProps) {
    const navigate = useNavigate();

    // Estados para paginação
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);

    // Navegação para a página de detalhes
    const handleRowClick = (id: string) => {
        navigate(`/abandoned/${id}`);
    };

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Filtragem com base nas props recebidas
    const filteredData = carts.filter((cart) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            (cart.name || "").toLowerCase().includes(term) ||
            (cart.email || "").toLowerCase().includes(term) ||
            cart.items.some(item => item.title.toLowerCase().includes(term))
        );
    });

    // Cálculo para paginação
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    {/* Table Header */}
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Cliente
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Valor
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Produto
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data da Transação
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    {/* Table Body */}
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {isLoading ? (
                            // Estado de carregamento
                            Array(5).fill(0).map((_, index) => (
                                <TableRow key={`loading-${index}`}>
                                    {Array(6).fill(0).map((_, cellIndex) => (
                                        <TableCell key={`loading-cell-${cellIndex}`} className="px-5 py-4">
                                            <div className="h-5 bg-gray-200 animate-pulse rounded dark:bg-gray-700"></div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : filteredData.length === 0 ? (
                            // Mensagem quando não há dados
                            <TableRow>
                                <TableCell className="px-5 py-8 text-center text-gray-500 dark:text-gray-400" colSpan={6}>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="flex flex-col items-center justify-center py-10">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search-x mb-3 size-10 text-gray-400 dark:text-gray-600">
                                                <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0"></path>
                                                <path d="M21 21l-6-6"></path>
                                                <path d="M10 7v6"></path>
                                                <path d="M7 10h6"></path>
                                            </svg>
                                            <p className="text-lg font-medium">Nenhum abandono encontrado para o período selecionado</p>
                                        </div>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : (
                            // Renderização dos dados
                            currentItems.map((cart) => (
                                <TableRow
                                    key={cart.id}
                                    onClick={() => handleRowClick(cart.id)}
                                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-white/[0.02] transition-colors"
                                >
                                    <TableCell className="px-5 py-4 text-start">
                                        <div className="flex text-xs truncate overflow-x-hidden text-[#232323] dark:text-[#797979] lg:!flex-[1.2]">
                                            <div className="flex flex-col w-full lg:dark:border-neutral-900 border-neutral-200">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-9 flex items-center justify-center  dark:bg-[#0F0F0F] bg-neutral-200 border dark:border-neutral-800 border-neutral-200  rounded-full">
                                                        <p className="text-md dark:text-white  text-black font-bold uppercase p-1">{cart.name?.charAt(0).toUpperCase()}</p>
                                                    </div>
                                                    <div className="flex-col hidden lg:flex">
                                                        <p className="text-black dark:text-white font-medium text-sm ">{cart.name}</p>
                                                        <p className="text-xs">{cart.email}</p>
                                                    </div>
                                                    <div className="flex-col flex lg:hidden">
                                                        <p className="text-black text-wrap text-left line-clamp-2 dark:text-white font-medium text-sm ">{cart.name}</p>
                                                        <p className="text-xs">{cart.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        <div className="flex items-center gap-2">
                                            <div className="flex flex-col">
                                                {formatCurrency(cart.items[0]?.price)}
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {cart.items[0]?.title
                                            ? (cart.items[0].title.length > 20
                                                ? `${cart.items[0].title.substring(0, 20)}...`
                                                : cart.items[0].title)
                                            : 'Produto indisponível'}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 text-start text-theme-sm dark:text-gray-400">
                                        {formatDate(cart.updatedAt)}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}